export type Signal = "metrics" | "logs" | "traces";

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && !location.pathname.startsWith("/sign-in")) {
    location.assign(`/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`);
    return new Promise<T>(() => {});
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
  return data as T;
}

function qs(params: Record<string, string | number | undefined | null>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : "";
}

export interface Stats {
  tokens: { input: number; output: number; cache_read: number; cache_creation: number; total: number };
  cost: { total: number };
  active_time: { total: number };
  sessions: { total: number };
  lines: { added: number; removed: number };
  routing: { pass: { c: number }; drop: { c: number } };
}

export interface TimeseriesPoint {
  bucket: number;
  grp: string | null;
  value: number;
}

export interface Session {
  session_id: string | null;
  project: string | null;
  model: string | null;
  started: number;
  last_active: number;
  tokens: number;
  cost: number;
}

export interface ProjectRow {
  project: string;
  sessions: number;
  last_active: number;
  tokens: number;
  cost: number;
}

export type RouteReason = "no_handler" | "handler" | "error";

type Row = { id: number; ts: number; route: "pass" | "drop"; route_reason: RouteReason | null; forwarded: number; session_id: string | null; project: string | null };
export type MetricRow = Row & { name: string; value: number; labels: string };
export type LogRow = Row & { body: string; severity: string | null; severity_number: number; attributes: string; resource: string };
export type TraceRow = Row & {
  name: string;
  trace_id: string;
  span_id: string;
  parent_span_id: string | null;
  kind: number;
  duration_ns: number;
  attributes: string;
  resource: string;
};
export type TelemetryRow = MetricRow | LogRow | TraceRow;

export interface Script {
  id: number;
  target: string;
  title: string;
  code: string;
  enabled: boolean;
  position: number;
  created_at: number;
  updated_at: number;
  errors: number;
  lastError: string | null;
  hits: number;
  compileError: string | null;
}

export type ScriptInput = Partial<Pick<Script, "title" | "code" | "enabled">>;

export interface Target {
  name: string;
  signal: Signal;
  count: number;
  last_seen: number;
  handlers: number;
  enabled_handlers: number;
}

/** The plain object user code sees as `record`. */
export interface ScriptRecord {
  signal: Signal;
  name: string;
  value?: number;
  body?: string;
  severity_number?: number;
  kind?: number;
  duration_ms?: number;
  ts: number;
  attributes: Record<string, unknown>;
  resource: Record<string, unknown>;
  [extra: string]: unknown;
}

export type Sample = ScriptRecord & { id: number; route: "pass" | "drop" };

export interface TestResult {
  id: number | string;
  before: ScriptRecord;
  after: ScriptRecord | null;
  dropped: boolean;
  reason: "handler" | "error" | null;
  error?: string;
  ms: number;
}

export interface TestSummary {
  total: number;
  dropped: number;
  changed: number;
  errors: number;
  firstError: string | null;
  maxMs: number;
}

export interface TestResponse {
  results: TestResult[];
  summary?: TestSummary;
}

export interface Config {
  retention_hours: number;
  forward_enabled: boolean;
  forward_active: boolean;
}

export type DownstreamSource = "intercepted" | "fabricated" | "all";
export interface DownstreamStats {
  sent: number;
  failed: number;
  lastStatus: number | null;
  lastError: string | null;
  lastAt: number | null;
}
export type DownstreamKind = "http" | "fake";
export type DownstreamProtocol = "json" | "protobuf";
export interface Downstream {
  id: number;
  kind: DownstreamKind;
  name: string;
  source: DownstreamSource;
  endpoint: string;
  headers: string;
  protocol: DownstreamProtocol;
  enabled: boolean;
  created_at: number;
  updated_at: number;
  stats: DownstreamStats;
}
export type DownstreamInput = Partial<Pick<Downstream, "kind" | "name" | "source" | "endpoint" | "headers" | "protocol" | "enabled">>;
export interface DownstreamCapture {
  id: number;
  downstream_id: number;
  ts: number;
  signal: Signal;
  source: "intercepted" | "fabricated";
  records: number;
  size: number;
  truncated: boolean;
}
export type DownstreamCaptureDetail = DownstreamCapture & { payload: string };

export interface Storage {
  db_bytes: number;
  wal_bytes: number;
  retention_hours: number;
  oldest_log: number | null;
  rows: { metrics: number; logs: number; traces: number; attr_sets: number; seen_names: number };
}

export interface StorageSample {
  ts: number;
  db_bytes: number;
  wal_bytes: number;
  metrics: number;
  logs: number;
  traces: number;
}

export type DataSource = "real" | "fabricated";

export type TelemetryFilter = {
  source?: DataSource;
  q?: string;
  name?: string;
  route?: string;
  project?: string;
  before?: number;
  limit?: number;
};

export interface Fabricator {
  id: number;
  name: string;
  signal: Signal;
  target: string;
  config: string;
  enabled: boolean;
  interval_ms: number;
  group: string | null;
  starts_at: number | null;
  ends_at: number | null;
  weekly_schedule: string | null;
  last_run_at: number | null;
  created_at: number;
  updated_at: number;
}

export type FabricatorInput = Partial<Pick<Fabricator, "name" | "config" | "enabled" | "interval_ms" | "group" | "starts_at" | "ends_at">>;

export interface SimRecipeResult {
  id: number;
  name: string;
  signal: Signal;
  target: string;
  interval_ms: number;
  ticks: number;
  records: number;
  sum: number | null;
  min: number | null;
  max: number | null;
  avg: number | null;
  series: number[];
  breakdown: { value: string; sum: number; records: number }[];
  error?: string;
}

export interface SimResult {
  from: number;
  to: number;
  hours: number;
  bucket_s: number;
  recipes: SimRecipeResult[];
  total_records: number;
  weekly_cost_cap: number | null;
}

export interface GroupScheduleResult {
  group: string;
  count: number;
  starts_at: number | null;
  ends_at: number | null;
  weekly_schedule: string | null;
  enabled: boolean;
}

export interface FabricatorStatus {
  id: number;
  name: string;
  signal: string;
  target: string;
  enabled: boolean;
  running: boolean;
  interval_ms: number;
  group: string | null;
  starts_at: number | null;
  ends_at: number | null;
  weekly_schedule: string | null;
  /** Server verdict for now, including jitter: false when a date limit or the weekly plan says idle. */
  in_window: boolean;
  /** Inside the day's window but in a random pause (in_window is false then). */
  paused: boolean;
  /** The group's trigger rule is open: running outside the plan because of real records. */
  triggered: boolean;
  /** The group has a trigger rule and its weekly budget is used up. */
  budget_reached: boolean;
  last_run_at: number | null;
}

export interface TriggerCondition {
  attr: string;
  value: string;
}
export type TriggerMatch = "all" | "any";

export interface GroupTrigger {
  enabled: boolean;
  match: TriggerMatch;
  conditions: TriggerCondition[];
  minutes: number;
  /** Unix seconds. */
  triggered_until: number | null;
  budget: number | null;
  cap: number | null;
  spent: number;
  budget_reached: boolean;
}

export const api = {
  stats: (since: number, project?: string, source?: DataSource) =>
    request<Stats>("GET", `/api/stats${qs({ since, project, source })}`),
  timeseries: (metric: string, since: number, groupBy: string, interval: number, project?: string, source?: DataSource) =>
    request<TimeseriesPoint[]>(
      "GET",
      `/api/timeseries${qs({ metric, since, group_by: groupBy, interval, project, source })}`,
    ),
  sessions: (since: number, project?: string, source?: DataSource) =>
    request<Session[]>("GET", `/api/sessions${qs({ since, project, source })}`),
  projects: (since: number, source?: DataSource | "all") =>
    request<ProjectRow[]>("GET", `/api/projects${qs({ since, source })}`),

  telemetry: <T extends TelemetryRow>(signal: Signal, filter: TelemetryFilter) =>
    request<T[]>("GET", `/api/telemetry/${signal}${qs(filter)}`),
  names: (signal: Signal) => request<{ name: string; count: number }[]>("GET", `/api/telemetry/names${qs({ signal })}`),

  targets: () => request<Target[]>("GET", "/api/targets"),
  scripts: () => request<Script[]>("GET", "/api/scripts"),
  createScript: (s: ScriptInput & { target: string }) => request<Script>("POST", "/api/scripts", s),
  updateScript: (id: number, s: ScriptInput) => request<Script>("PUT", `/api/scripts/${id}`, s),
  deleteScript: (id: number) => request("DELETE", `/api/scripts/${id}`),
  reorderScripts: (target: string, ids: number[]) => request("POST", "/api/scripts/reorder", { target, ids }),
  scriptTemplate: (target: string) => request<{ code: string }>("GET", `/api/scripts/template${qs({ target })}`),
  samples: (target: string, q: string, limit = 40) => request<Sample[]>("GET", `/api/samples${qs({ target, q, limit })}`),
  testScript: (body: { target: string; code: string; records?: ScriptRecord[]; runOnLatest?: number }) =>
    request<TestResponse>("POST", "/api/scripts/test", body),

  storage: () => request<Storage>("GET", "/api/storage"),
  storageHistory: (hours: number) => request<StorageSample[]>("GET", `/api/storage/history?hours=${hours}`),
  config: () => request<Config>("GET", "/api/config"),
  updateConfig: (c: Partial<Pick<Config, "retention_hours" | "forward_enabled">>) =>
    request<Config>("PUT", "/api/config", c),
  downstreams: () => request<Downstream[]>("GET", "/api/downstreams"),
  createDownstream: (d: DownstreamInput & Pick<Downstream, "name">) => request<Downstream>("POST", "/api/downstreams", d),
  updateDownstream: (id: number, d: DownstreamInput) => request<Downstream>("PUT", `/api/downstreams/${id}`, d),
  deleteDownstream: (id: number) => request("DELETE", `/api/downstreams/${id}`),
  downstreamCaptures: (id: number) => request<DownstreamCapture[]>("GET", `/api/downstreams/${id}/captures?limit=200`),
  downstreamCapture: (id: number, captureId: number) =>
    request<DownstreamCaptureDetail>("GET", `/api/downstreams/${id}/captures/${captureId}`),
  clearDownstreamCaptures: (id: number) => request("DELETE", `/api/downstreams/${id}/captures`),
  fabricators: () => request<Fabricator[]>("GET", "/api/fabricators"),
  fabricatorStatus: () => request<FabricatorStatus[]>("GET", "/api/fabricators/status"),
  createFabricator: (f: { name: string; signal: Signal; target: string; config: string; enabled?: boolean; interval_ms?: number; group?: string | null; starts_at?: number | null; ends_at?: number | null }) =>
    request<Fabricator>("POST", "/api/fabricators", f),
  updateFabricator: (id: number, f: FabricatorInput) => request<Fabricator>("PUT", `/api/fabricators/${id}`, f),
  deleteFabricator: (id: number) => request("DELETE", `/api/fabricators/${id}`),
  toggleFabricatorGroup: (group: string, enabled: boolean) =>
    request<{ group: string; enabled: boolean; count: number }>("POST", `/api/fabricators/group/${encodeURIComponent(group)}/toggle`, { enabled }),
  scheduleFabricatorGroup: (group: string, starts_at: number | null, ends_at: number | null, weekly: unknown | null = null) =>
    request<GroupScheduleResult>("POST", `/api/fabricators/group/${encodeURIComponent(group)}/schedule`, { starts_at, ends_at, weekly }),
  clearFabricatorGroupSchedule: (group: string) =>
    request<GroupScheduleResult>("DELETE", `/api/fabricators/group/${encodeURIComponent(group)}/schedule`),
  fabricatorGroupTrigger: (group: string) =>
    request<GroupTrigger>("GET", `/api/fabricators/group/${encodeURIComponent(group)}/trigger`),
  setFabricatorGroupTrigger: (group: string, t: { enabled: boolean; match: TriggerMatch; conditions: TriggerCondition[]; minutes: number }) =>
    request<GroupTrigger>("PUT", `/api/fabricators/group/${encodeURIComponent(group)}/trigger`, t),
  toggleFabricator: (id: number) => request<Fabricator>("POST", `/api/fabricators/${id}/toggle`),
  analyzeFabricator: (signal: Signal, target: string) =>
    request<Record<string, unknown>>("GET", `/api/fabricators/analyze${qs({ signal, target })}`),
  simulateFabricators: (ids: number[], hours: number) =>
    request<SimResult>("POST", "/api/fabricators/simulate", { ids, hours }),
  previewFabricator: (body: { signal: Signal; target: string; config: string; count?: number }) =>
    request<Record<string, unknown>[]>("POST", "/api/fabricators/preview", body),
};
